import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
    Shield, ChevronDown, Upload, Download, Search, AlertTriangle,
    BarChart3, Clock, XCircle, FileText, CalendarDays, Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_OPTIONS = [
    { value: 'en_attente', label: 'En attente', dot: 'bg-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
    { value: 'demande', label: 'Demande', dot: 'bg-blue-500', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
    { value: 'refuse', label: 'Refuse', dot: 'bg-red-500', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600' },
    { value: 'accorde', label: 'Accorde', dot: 'bg-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
    { value: 'hors_delai', label: 'Hors delai', dot: 'bg-gray-500', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600' },
];

export default function AOT() {
    const navigate = useNavigate();
    const [allItems, setAllItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDrop, setOpenDrop] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadCtx, setUploadCtx] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadSaving, setUploadSaving] = useState(false);

    const fetchItems = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/aot-list`, { withCredentials: true });
            setAllItems(res.data);
        } catch {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchItems(); }, []);

    // Stats
    const stats = useMemo(() => {
        const s = { total: 0, en_attente: 0, demande: 0, hors_delai: 0, refuse: 0, urgent: 0 };
        allItems.forEach(i => {
            if (i.statut === 'en_attente') { s.en_attente++; s.total++; }
            else if (i.statut === 'demande') { s.demande++; s.total++; }
            else if (i.statut === 'hors_delai') s.hors_delai++;
            else if (i.statut === 'refuse') s.refuse++;
            if (i.is_urgent) s.urgent++;
        });
        return s;
    }, [allItems]);

    // Filtered + sorted list
    const filteredItems = useMemo(() => {
        let items = [...allItems];
        // Exclude accorde/refuse/hors_delai from list (unless explicitly filtered)
        if (!statusFilter) {
            items = items.filter(i => !['accorde', 'refuse', 'hors_delai'].includes(i.statut));
        } else {
            items = items.filter(i => i.statut === statusFilter);
        }
        // Search
        if (search) {
            const q = search.toLowerCase();
            items = items.filter(i =>
                (i.client_nom || '').toLowerCase().includes(q) ||
                (i.client_email || '').toLowerCase().includes(q) ||
                (i.client_telephone || '').includes(q) ||
                (i.devis_numero || '').toLowerCase().includes(q) ||
                (i.adresse || '').toLowerCase().includes(q)
            );
        }
        // Sort: urgent first, then by closest date
        items.sort((a, b) => {
            if (a.is_urgent !== b.is_urgent) return b.is_urgent ? 1 : -1;
            const da = a.date ? new Date(a.date) : new Date('9999-12-31');
            const db = b.date ? new Date(b.date) : new Date('9999-12-31');
            return da - db;
        });
        return items;
    }, [allItems, search, statusFilter]);

    const handleStatutChange = async (clientId, type, newStatut) => {
        if (newStatut === 'accorde' || newStatut === 'refuse') {
            setUploadCtx({ clientId, type, statut: newStatut });
            setUploadFile(null);
            setUploadOpen(true);
            return;
        }
        try {
            // Day-based item ("jour_N") → /jour/statut endpoint with jour_index.
            // Legacy item ("depart"/"arrivee") → /statut endpoint with type.
            if (typeof type === 'string' && type.startsWith('jour_')) {
                const jourIndex = parseInt(type.split('_')[1], 10);
                await axios.post(`${API_URL}/api/clients/${clientId}/aot/jour/statut`,
                    { jour_index: jourIndex, statut: newStatut },
                    { withCredentials: true });
            } else {
                await axios.post(`${API_URL}/api/clients/${clientId}/aot/statut`,
                    { type, statut: newStatut },
                    { withCredentials: true });
            }
            toast.success('Statut mis a jour');
            fetchItems();
        } catch {
            toast.error('Erreur');
        }
    };

    const handleUploadAccorde = async () => {
        if (!uploadFile) { toast.error('Photo ou fichier obligatoire'); return; }
        setUploadSaving(true);
        try {
            const reader = new FileReader();
            const base64 = await new Promise((resolve) => { reader.onload = () => resolve(reader.result); reader.readAsDataURL(uploadFile); });
            const uploadRes = await axios.post(`${API_URL}/api/clients/${uploadCtx.clientId}/livraison/upload`, { file_data: base64, file_name: uploadFile.name }, { withCredentials: true });
            if (typeof uploadCtx.type === 'string' && uploadCtx.type.startsWith('jour_')) {
                const jourIndex = parseInt(uploadCtx.type.split('_')[1], 10);
                await axios.post(`${API_URL}/api/clients/${uploadCtx.clientId}/aot/jour/statut`, {
                    jour_index: jourIndex, statut: uploadCtx.statut, justificatif_url: uploadRes.data.url,
                }, { withCredentials: true });
            } else {
                await axios.post(`${API_URL}/api/clients/${uploadCtx.clientId}/aot/statut`, {
                    type: uploadCtx.type, statut: uploadCtx.statut, justificatif_url: uploadRes.data.url,
                }, { withCredentials: true });
            }
            toast.success('Statut mis a jour');
            setUploadOpen(false);
            fetchItems();
        } catch {
            toast.error('Erreur');
        } finally {
            setUploadSaving(false);
        }
    };

    const STAT_BLOCKS = [
        { label: 'Total AOT', value: stats.total, icon: BarChart3, borderCls: 'border-gray-200', iconCls: 'text-gray-500', valueCls: 'text-gray-800' },
        { label: 'En attente', value: stats.en_attente, icon: Clock, borderCls: 'border-amber-200', iconCls: 'text-amber-500', valueCls: 'text-amber-600' },
        { label: 'Demande', value: stats.demande, icon: FileText, borderCls: 'border-blue-200', iconCls: 'text-blue-500', valueCls: 'text-blue-600' },
        { label: 'Hors delai', value: stats.hors_delai, icon: XCircle, borderCls: 'border-orange-200', iconCls: 'text-orange-500', valueCls: 'text-orange-600' },
        { label: 'Refuse', value: stats.refuse, icon: XCircle, borderCls: 'border-red-200', iconCls: 'text-red-500', valueCls: 'text-red-600' },
        { label: 'Urgent', value: stats.urgent, icon: AlertTriangle, borderCls: 'border-pink-200', iconCls: 'text-pink-500', valueCls: 'text-pink-600' },
    ];

    return (
        <Layout>
            <div className="space-y-5" data-testid="aot-page">
                {/* Header */}
                <div>
                    <h1 className="text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>AOT</h1>
                    <p className="text-sm text-gray-400 mt-0.5">Autorisations de stationnement</p>
                </div>

                {/* Stats blocks */}
                <div className="grid grid-cols-6 gap-3" data-testid="aot-stats">
                    {STAT_BLOCKS.map((b, i) => (
                        <div key={i} className={`bg-white rounded-xl border-2 ${b.borderCls} p-4 flex flex-col gap-1`}>
                            <div className="flex items-center gap-2">
                                <b.icon className={`w-4 h-4 ${b.iconCls}`} />
                                <span className={`text-2xl font-bold ${b.valueCls}`}>{b.value}</span>
                            </div>
                            <span className="text-xs text-gray-500">{b.label}</span>
                        </div>
                    ))}
                </div>

                {/* Search + Filter */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input placeholder="Rechercher (nom, adresse, email, telephone, devis)..."
                            value={search} onChange={e => setSearch(e.target.value)}
                            className="pl-9 text-sm" data-testid="aot-search" />
                    </div>
                    <div className="relative min-w-[180px]">
                        <Filter className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                            className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 appearance-none"
                            data-testid="aot-status-filter">
                            <option value="">Tous les statuts</option>
                            {STATUT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                </div>

                {/* AOT List */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                        <Shield className="w-4 h-4 text-[#1B365D]" />
                        <h2 className="text-sm font-bold text-[#1B365D]">Liste des AOT ({filteredItems.length})</h2>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1B365D]" /></div>
                    ) : filteredItems.length === 0 ? (
                        <div className="text-center py-12">
                            <Shield className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                            <p className="text-gray-400 text-sm">Aucune AOT trouvee</p>
                        </div>
                    ) : (
                        <div>
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-100">
                                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Client</th>
                                        <th className="text-left px-3 py-3 text-xs font-semibold text-gray-400 uppercase">Type</th>
                                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Adresse</th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Date</th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Statut</th>
                                        <th className="text-center px-3 py-3 text-xs font-semibold text-gray-400 uppercase">Fichier</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredItems.map((item, idx) => {
                                        const currentStatut = item.statut || 'en_attente';
                                        const sCfg = STATUT_OPTIONS.find(o => o.value === currentStatut) || STATUT_OPTIONS[0];
                                        const dropKey = `${item.client_id}-${item.type}-${idx}`;
                                        return (
                                            <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-testid={`aot-row-${idx}`}>
                                                <td className="px-5 py-3.5">
                                                    <div className="flex items-center gap-2">
                                                        <button onClick={() => navigate(`/clients/${item.client_id}`)} className="text-sm font-semibold text-[#1B365D] hover:underline">
                                                            {item.client_nom}
                                                        </button>
                                                        {item.is_urgent && (
                                                            <Badge className="bg-red-500 text-white text-[9px] px-1.5 py-0.5 font-bold animate-pulse" data-testid={`urgent-badge-${idx}`}>URGENT</Badge>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-3.5">
                                                    {(() => {
                                                        const dir = item.direction || item.type;
                                                        const isDepart = dir === 'depart';
                                                        return (
                                                            <Badge className={`text-[10px] ${isDepart ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-100 text-red-600 border-red-200'} border`}>
                                                                {isDepart ? 'Partir' : 'Arriver'}
                                                            </Badge>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="px-5 py-3.5">
                                                    <p className="text-sm text-gray-600 max-w-xs truncate">{item.adresse || '-'}</p>
                                                </td>
                                                <td className="px-4 py-3.5">
                                                    <div className="flex items-center gap-1.5 text-sm text-gray-500">
                                                        <CalendarDays className="w-3 h-3 text-gray-400" />
                                                        {item.date ? new Date(item.date + 'T00:00').toLocaleDateString('fr-FR') : '-'}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3.5">
                                                    <div className="relative">
                                                        <button onClick={() => setOpenDrop(openDrop === dropKey ? null : dropKey)}
                                                            className={`flex items-center gap-2 ${sCfg.bg} ${sCfg.border} border rounded-xl px-3 py-1.5 text-xs font-semibold shadow-sm hover:shadow-md transition-all min-w-[130px]`}>
                                                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sCfg.dot}`} />
                                                            <span className={`${sCfg.text} flex-1 text-left`}>{sCfg.label}</span>
                                                            <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${openDrop === dropKey ? 'rotate-180' : ''}`} />
                                                        </button>
                                                        {openDrop === dropKey && (
                                                            <>
                                                                <div className="fixed inset-0 z-30" onClick={() => setOpenDrop(null)} />
                                                                <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-40 min-w-[160px]">
                                                                    {STATUT_OPTIONS.map(o => (
                                                                        <button key={o.value} onClick={() => { setOpenDrop(null); handleStatutChange(item.client_id, item.type, o.value); }}
                                                                            className={`w-full text-left px-4 py-2 text-xs flex items-center gap-3 transition-colors ${currentStatut === o.value ? 'bg-gray-50 font-bold' : 'hover:bg-gray-50'}`}>
                                                                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${o.dot}`} />
                                                                            <span className="text-gray-700 flex-1">{o.label}</span>
                                                                            {currentStatut === o.value && <span className="text-[#1B365D]">&#10003;</span>}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-3.5 text-center">
                                                    {item.justificatif_url ? (
                                                        <a href={`${API_URL}${item.justificatif_url}`} target="_blank" rel="noreferrer"
                                                            className="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700">
                                                            <Download className="w-3 h-3" /> AOT
                                                        </a>
                                                    ) : <span className="text-xs text-gray-300">-</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Upload Dialog */}
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                <DialogContent className="max-w-md p-0" data-testid="aot-upload-modal">
                    <DialogTitle className="sr-only">Justificatif AOT</DialogTitle>
                    <div className="p-6 space-y-5">
                        <h2 className="text-lg font-bold text-[#1B365D]">Justificatif requis</h2>
                        <p className="text-sm text-gray-500">Pour passer au statut "Accorde", veuillez fournir un justificatif.</p>
                        <div className="border-2 border-dashed border-amber-300 rounded-xl p-5 bg-amber-50/30">
                            <label className="flex flex-col items-center gap-2 cursor-pointer">
                                <Upload className="w-6 h-6 text-amber-500" />
                                <span className="text-sm text-amber-700 font-medium">
                                    {uploadFile ? uploadFile.name : 'Upload ou photo obligatoire'}
                                </span>
                                <input type="file" className="hidden" accept="image/*,.pdf"
                                    onChange={e => setUploadFile(e.target.files[0])} data-testid="aot-upload-input" />
                            </label>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setUploadOpen(false)}>Annuler</Button>
                            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleUploadAccorde} disabled={uploadSaving} data-testid="aot-upload-submit">
                                {uploadSaving ? 'Enregistrement...' : 'Valider'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
